import { useEffect } from "react";
import { useDispatch } from "react-redux";
import firebase from "../config/firebase";
import { refreshAuth, logout } from "../store/actions/authActions";

/**
 * AuthStateManager Component
 * Manages Firebase authentication state persistence and synchronization
 * 
 * Key Responsibilities:
 * 1. Listen to Firebase auth state changes (onAuthStateChanged)
 * 2. Restore authentication after page refresh
 * 3. Sync email verification status with Firestore
 * 4. Handle authentication expiration
 * 
 * This component doesn't render anything - it's purely for side effects
 */
const AuthStateManager = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Set up Firebase auth state listener
    const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        // User is signed in
        console.log("Auth state changed: User authenticated", user.uid);

        try {
          // Reload user to get latest email verification status
          await user.reload();

          // Check if user document exists in Firestore
          const userDoc = await firebase
            .firestore()
            .collection("users")
            .doc(user.uid)
            .get();

          if (userDoc.exists) {
            const userData = userDoc.data();

            // Only proceed if user is an athlete and not blocked
            if (userData.role === "athlete" && !userData.blocked) {
              // Sync emailVerified status between Firebase Auth and Firestore
              if (user.emailVerified !== userData.emailVerified) {
                await firebase
                  .firestore()
                  .collection("users")
                  .doc(user.uid)
                  .update({
                    emailVerified: user.emailVerified,
                  });
                console.log(
                  "Email verification status synced:",
                  user.emailVerified
                );
              }

              // Refresh auth state in Redux
              dispatch(refreshAuth(user.uid));
            } else {
              // User is not an athlete or is blocked - sign them out
              console.log("User is blocked or not an athlete, signing out");
              firebase.auth().signOut();
              dispatch(logout());
            }
          } else {
            // User document doesn't exist - sign them out
            console.log("User document not found, signing out");
            dispatch(logout());
          }
        } catch (error) {
          console.error("Error in auth state handler:", error);

          // Handle token expiration or other auth errors
          if (
            error.code === "auth/user-token-expired" ||
            error.code === "auth/user-disabled" ||
            error.code === "auth/user-not-found"
          ) {
            console.log("Authentication error, signing out");
            dispatch(logout());
          }
        }
      } else {
        // User is signed out
        console.log("Auth state changed: User signed out");
        dispatch(logout());
      }
    });

    // Cleanup listener on component unmount
    return () => {
      console.log("Cleaning up auth state listener");
      unsubscribe();
    };
  }, [dispatch]);

  // This component doesn't render anything
  return null;
};

export default AuthStateManager;
